import React from 'react';

import { formatISODate } from '@aspid/utils/date';
import { Box, Paper, Stack } from '@mui/material';

import { borders, shape } from '@app/theme';

import { AnalysisDate } from './AnalysisDate';
import { Analyst } from './Analyst';
import { Materials } from './Materials';
import { ReportTitle } from './ReportTitle';

import type { InvestmentReportHeroProperties } from './InvestmentReportHero.types';

export const InvestmentReportHero: React.FC<InvestmentReportHeroProperties> = ({
  projectName,
  analysisDate,
  fileName,
  onMaterialsDownload,
  sx,
  dataTest = 'investment_report_hero',
}) => {
  // Форматируем дату из ISO в dd/MM/yyyy
  const formattedDate = React.useMemo(() => {
    if (!analysisDate) return;
    return formatISODate(analysisDate, 'dd/MM/yyyy');
  }, [analysisDate]);

  // Если нет данных, не отображаем hero
  if (!projectName && !analysisDate && !fileName) {
    return;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 4, md: 5 },
        borderRadius: `${shape.borderRadius}px`,
        border: borders.default,
        backgroundColor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      data-testid={dataTest}
    >
      {/* Контент */}
      <Stack spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
        {/* Заголовок с названием проекта */}
        {projectName && (
          <ReportTitle
            projectName={projectName}
            dataTest={`${dataTest}_title`}
          />
        )}

        {/* Метаданные в сетке */}
        {(analysisDate || fileName) && (
          <Box
            data-testid={`${dataTest}_metadata`}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(3, 1fr)',
              },
              gap: { xs: 3, md: 4 },
              pt: 3,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {formattedDate && (
              <AnalysisDate
                date={formattedDate}
                dataTest={`${dataTest}_date`}
              />
            )}
            {fileName && (
              <Materials
                fileName={fileName}
                onDownload={onMaterialsDownload}
                dataTest={`${dataTest}_materials`}
              />
            )}
            <Analyst dataTest={`${dataTest}_analyst`} />
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
